import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useManagerViewPermission } from '../../hooks/useManagerViewPermission';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Search, Plus, Filter, X, ChevronDown, Loader2, Calendar, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { employeeApi, departmentApi, leaveApi } from '../../services/api';
import { EmployeeStatus } from '../../types/hr';

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const EmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const { isUserView, canSearchEmployees, canFilterByDepartment, canFilterByStatus, canAddEmployee, canViewLeaveBalance, canEditEmployee } = useManagerViewPermission();
  const [searchInput, setSearchInput] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Debounce search query - wait 500ms after user stops typing
  const debouncedSearchQuery = useDebounce(searchInput, 500);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedDepartments, selectedStatuses, debouncedSearchQuery]);

  // Fetch employees from API - only triggers when debounced value changes
  const { data, isLoading: employeesLoading, isFetching: employeesFetching, error: employeesError } = useQuery<any>({
    queryKey: ['employees', selectedStatuses, selectedDepartments, debouncedSearchQuery, page],
    queryFn: () => {
      const params = {
        status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
        department_id: selectedDepartments.length > 0 ? selectedDepartments.join(',') : undefined,
        search: debouncedSearchQuery || undefined,
        offset: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      };

      return employeeApi.getCombined(params);
    },
    // Keep previous data while fetching new data to prevent flickering
    placeholderData: (previousData) => previousData,
  });

  const employees = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Fetch departments from API
  const { data: departments = [], isLoading: departmentsLoading } = useQuery<any[]>({
    queryKey: ['departments'],
    queryFn: () => departmentApi.getAll() as Promise<any[]>,
  });

  // Fetch leave policies and requests
  const { data: policies = [] } = useQuery<any[]>({
    queryKey: ['leavePolicies'],
    queryFn: () => leaveApi.policies.getAll() as Promise<any[]>,
  });

  const { data: allLeaveRequests = [] } = useQuery<any[]>({
    queryKey: ['allLeaveRequests'],
    queryFn: () => leaveApi.requests.getAll({}) as Promise<any[]>,
  });

  // Calculate leave balance for an employee
  const calculateLeaveBalance = (employeeId: string) => {
    if (!policies || policies.length === 0) {
      return { total: 0, used: 0, available: 0, pending: 0 };
    }

    let totalEntitled = 0;
    let totalUsed = 0;
    let totalAvailable = 0;
    let totalPending = 0;

    policies.forEach((policy: any) => {
      const approvedLeaves = allLeaveRequests.filter(
        (req: any) => req.employee_id === employeeId && req.leave_policy_id === policy.id && req.status === 'APPROVED'
      ).reduce((sum: number, req: any) => sum + (req.days_count || 1), 0);

      const pendingLeaves = allLeaveRequests.filter(
        (req: any) => req.employee_id === employeeId && req.leave_policy_id === policy.id && req.status === 'PENDING'
      ).reduce((sum: number, req: any) => sum + (req.days_count || 1), 0);

      const policyTotal = policy.days_per_year || 0;
      const available = Math.max(0, policyTotal - approvedLeaves - pendingLeaves);

      totalEntitled += policyTotal;
      totalUsed += approvedLeaves;
      totalPending += pendingLeaves;
      totalAvailable += available;
    });

    return {
      total: totalEntitled,
      used: totalUsed,
      available: totalAvailable,
      pending: totalPending,
    };
  };

  // Helper functions
  const getFullName = (firstName: string, lastName?: string): string => {
    return lastName ? `${firstName} ${lastName}` : firstName;
  };

  const getInitials = (firstName: string, lastName?: string): string => {
    if (!lastName) return firstName.charAt(0).toUpperCase();
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  const getDepartmentById = (id: string) => {
    return departments.find(dept => dept.id === id);
  };

  const handleEmployeeClick = (employeeId: string) => {
    navigate(`/employees/${employeeId}`);
  };

  const toggleDepartment = (deptId: string) => {
    setSelectedDepartments(prev =>
      prev.includes(deptId)
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const clearFilters = () => {
    setSelectedDepartments([]);
    setSelectedStatuses([]);
    setSearchInput('');
  };

  const hasActiveFilters = selectedDepartments.length > 0 || selectedStatuses.length > 0 || searchInput;

  // Only show full-page loading on initial load (when there's no data yet)
  const isInitialLoading = employeesLoading && employees.length === 0;

  // Loading state - only for initial load
  if (isInitialLoading || departmentsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading employees...</span>
      </div>
    );
  }

  // Error state
  if (employeesError) {
    return (
      <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
        <p className="font-semibold">Error loading employees</p>
        <p className="text-sm mt-1">{employeesError.message || 'Please try again later'}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-6 p-6 custom-scrollbar">
     
     



      {/* --- Summary Tiles --- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Employees */}
        <Card className="border-none shadow-lg bg-gradient-to-br from-[#143A62] to-[#205081] text-white overflow-hidden relative group cursor-pointer">
          <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform duration-500">
            <Users className="w-24 h-24" />
          </div>
          <CardContent className="p-6 flex flex-col justify-between h-full relative z-10">
            <div>
              <h2 className="text-blue-100 text-xs font-semibold mb-1">Total Workforce</h2>
              <p className="text-blue-200 text-xs font-medium opacity-80">Overall company size</p>
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <span className="text-4xl font-bold block">{totalCount}</span>
              <div className="flex items-center justify-between text-blue-200 text-xs mt-2 border-t border-white/10 pt-3">
                <span className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  {employees.filter((e: any) => e.status === EmployeeStatus.ACTIVE).length} Active
                </span>
                <span className="opacity-70 font-semibold">
                  {totalCount > 0 ? Math.round((employees.filter((e: any) => e.status === EmployeeStatus.ACTIVE).length / totalCount) * 100) : 0}% Active
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Employees */}
        <Card className="border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-slate-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-500 dark:text-slate-400 text-xs font-semibold">Active Employees</p>
              <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-slate-100">{employees.filter((e: any) => e.status === EmployeeStatus.ACTIVE).length}</p>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Current active</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Onboarding Employees */}
        <Card className="border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-slate-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-500 dark:text-slate-400 text-xs font-semibold">Onboarding</p>
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <Plus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-slate-100">{employees.filter((e: any) => e.status === EmployeeStatus.ONBOARDING).length}</p>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400">In process</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Departments count */}
        <Card className="border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-slate-900">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-500 dark:text-slate-400 text-xs font-semibold">Departments</p>
              <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <Filter className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-800 dark:text-slate-100">{departments.length}</p>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Total units</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* --- Main Content Section --- */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
            <span className="text-blue-500">⚡</span>
            Employees List
          </h2>
          {!isUserView && canAddEmployee && (
            <Button
              onClick={() => navigate('/employees/new')}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          )}
        </div>

        <Card className="border border-gray-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden">
          {/* Controls Row */}
          <div className="flex flex-col md:flex-row items-center justify-between py-4 px-6 border-b border-gray-100 dark:border-slate-800 gap-4">
            <div className="flex flex-col md:flex-row items-center gap-6 w-full md:w-auto">
              {/* Search */}
              {!isUserView && canSearchEmployees && (
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search employees..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-10 pr-10 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 h-9 rounded-lg text-sm"
                  />
                  {employeesFetching && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                  )}
                </div>
              )}

              {/* Filters Popover style */}
              <div className="flex items-center gap-4">
                {canFilterByDepartment && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-[#2563eb] dark:text-blue-400 text-xs font-bold hover:text-blue-700 dark:hover:text-blue-300 uppercase tracking-wider focus:outline-none group">
                        <Filter className="h-3.5 w-3.5 fill-current" />
                        <span className="underline decoration-blue-500/30 underline-offset-4 group-hover:decoration-blue-700">Department</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                      <DropdownMenuLabel className="text-xs uppercase tracking-widest text-gray-400">Filter Departments</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {departments.map(dept => (
                        <DropdownMenuCheckboxItem
                          key={dept.id}
                          checked={selectedDepartments.includes(dept.id)}
                          onCheckedChange={() => toggleDepartment(dept.id)}
                          className="text-sm"
                        >
                          {dept.name}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {canFilterByStatus && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-[#2563eb] dark:text-blue-400 text-xs font-bold hover:text-blue-700 dark:hover:text-blue-300 uppercase tracking-wider focus:outline-none group">
                        <Filter className="h-3.5 w-3.5 fill-current" />
                        <span className="underline decoration-blue-500/30 underline-offset-4 group-hover:decoration-blue-700">Status</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                      <DropdownMenuLabel className="text-xs uppercase tracking-widest text-gray-400">Filter Status</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {Object.values(EmployeeStatus).map(status => (
                        <DropdownMenuCheckboxItem
                          key={status}
                          checked={selectedStatuses.includes(status)}
                          onCheckedChange={() => toggleStatus(status)}
                          className="text-sm"
                        >
                          {status}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-red-500 hover:text-red-600 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                    <X className="h-3.5 w-3.5" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-auto">
              {totalCount} total employees
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b-2 border-gray-50 dark:border-slate-800">
                  <TableHead className="text-[11px] font-bold text-gray-400 tracking-widest h-12">Employee</TableHead>
                  <TableHead className="text-[11px] font-bold text-gray-400 tracking-widest h-12">Designation</TableHead>
                  <TableHead className="text-[11px] font-bold text-gray-400 tracking-widest h-12">Department</TableHead>
                  <TableHead className="text-[11px] font-bold text-gray-400 tracking-widest h-12">Contact Info</TableHead>
                  {!isUserView && canViewLeaveBalance && <TableHead className="text-[11px] font-bold text-gray-400 tracking-widest h-12">Leave Balance</TableHead>}
                  <TableHead className="text-[11px] font-bold text-gray-400 tracking-widest h-12">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <div className="flex flex-col items-center">
                        <Users className="h-12 w-12 text-gray-200 dark:text-slate-700 mb-4" />
                        <h3 className="text-lg font-medium text-gray-700 dark:text-slate-300">No employees found</h3>
                        <p className="text-sm text-gray-500 dark:text-slate-500 mt-1">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => {
                    const department = employee.department_id ? getDepartmentById(employee.department_id) : null;
                    const leaveBalance = calculateLeaveBalance(employee.id);

                    // Determine status badge style
                    let statusBadge = "bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-400";
                    if (employee.status === EmployeeStatus.ACTIVE) {
                      statusBadge = "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
                    } else if (employee.status === EmployeeStatus.ONBOARDING) {
                      statusBadge = "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
                    } else if (employee.status === EmployeeStatus.OFFBOARDING) {
                      statusBadge = "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
                    }

                    return (
                      <TableRow
                        key={employee.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 border-b border-gray-100 dark:border-slate-800 transition-colors"
                        onClick={() => handleEmployeeClick(employee.id)}
                      >
                        <TableCell className="py-4">
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-10 w-10 border border-gray-100 dark:border-slate-700 shadow-sm">
                              <AvatarImage src={employee.avatar_url} />
                              <AvatarFallback className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold">
                                {getInitials(employee.first_name, employee.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="font-bold text-[#143A62] dark:text-slate-200">
                                {getFullName(employee.first_name, employee.last_name)}
                              </span>
                              <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
                                ID: #{employee.id.slice(0, 8).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{employee.job_title || '-'}</span>
                        </TableCell>
                        <TableCell className="py-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50">
                            {department?.name || 'General'}
                          </span>
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{employee.work_email || '-'}</span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-500">{employee.work_phone || '-'}</span>
                          </div>
                        </TableCell>
                        {!isUserView && canViewLeaveBalance && (
                          <TableCell className="py-4">
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-500">
                                  {leaveBalance.available} days
                                </span>
                                <span className="text-[10px] text-slate-400 uppercase font-semibold">available</span>
                              </div>
                            </div>
                          </TableCell>
                        )}
                        <TableCell className="py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusBadge}`}>
                            {employee.status === EmployeeStatus.ONBOARDING ? 'Onboarding' : employee.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
              Showing {Math.min((page - 1) * itemsPerPage + 1, totalCount)} to {Math.min(page * itemsPerPage, totalCount)} of {totalCount}
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full border-gray-200 dark:border-slate-700"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || employeesLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs font-bold text-slate-600 dark:text-slate-400 px-2">
                {page} / {Math.max(1, totalPages)}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full border-gray-200 dark:border-slate-700"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || employeesLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EmployeeList;
